import { useState, useCallback, useEffect, useRef } from 'react'

/**
 * Hook personnalisé pour gérer l'état d'une modale et fournir les méthodes nécessaires.
 * 
 * @param {boolean} initialState - État initial de la modale (ouverte ou fermée)
 * @returns {Object} Un objet contenant l'état de la modale et les méthodes pour la manipuler
 * @example
 * const { isOpen, show, hide, toggle, dialogRef } = Modal.useModal();
 * 
 * // Utilisation avec le composant Modal
 * <Modal open={isOpen} onClose={hide} ref={dialogRef}>
 *   Contenu de la modale
 * </Modal>
 */
const useModal = (initialState = false) => {
  const [isOpen, setIsOpen] = useState(initialState)
  const dialogRef = useRef(null)
  const hasBeenOpened = useRef(false)

  // Effet qui synchronise l'état React avec l'élément dialog natif
  useEffect(() => {
    if (isOpen && dialogRef.current) {
      // Vérifions si la modal n'est pas déjà ouverte avant d'appeler showModal()
      try {
        if (!dialogRef.current.open) {
          dialogRef.current.showModal()
          hasBeenOpened.current = true
        }
      } catch (error) {
        console.error('Erreur lors de l\'ouverture de la modal:', error)
      }
    } else if (!isOpen && dialogRef.current && hasBeenOpened.current) {
      // Ne fermer que si la modal a été ouverte auparavant
      try {
        dialogRef.current.close()
      } catch (error) {
        console.error('Erreur lors de la fermeture de la modal:', error)
      }
    }
  }, [isOpen])

  const handleShow = useCallback(() => {
    setIsOpen(true)
  }, [])

  const handleHide = useCallback(() => {
    setIsOpen(false)
  }, [])

  const toggle = useCallback(() => {
    setIsOpen(prevIsOpen => !prevIsOpen)
  }, [])

  return {
    isOpen,
    handleShow,
    handleHide,
    toggle,
    dialogRef
  }
}

export default useModal